/**
 * SF-160: extracted verbatim from the old Council `BuildingsTab.tsx` — no behavior change.
 * `BUILDING_BONUS` is needed by both the desktop and mobile Overview panels (desktop's table,
 * mobile's grouped bonus list); without this shared module, mobile would need its own copy of 18
 * client-side replicas of backend bonus formulas, exactly the kind of silently-desyncing
 * duplication `buildingsPercent.ts` was extracted to avoid. Only the desktop panel needs the two
 * planner builders + `PLANNER_BUILDING_ORDER`, but they're pure and side-effect-free, so it costs
 * nothing to keep them in the same shared module rather than a second file.
 */

import { BUILDING_GROUPS, BUILDING_MAP } from '../../constants/buildings';
import { formatNumber } from '../../utils/format';
import type { Construction, Exploration } from '../../types/province';

// ─── Bonus Computation (mirrors backend formulas, display-only) ──────────────

export function pctSimple(base: number, pctLand: number, be: number): number {
  return base * be * pctLand;
}

export function pctBased(base: number, pctLand: number, be: number): number {
  return base * be * pctLand * (1 - pctLand);
}

export const BUILDING_BONUS: Record<string, (count: number, pctLand: number, be: number) => string> = {
  FARM: (count, _pct, be) => `+${formatNumber(Math.round(count * 70 * be))} bushels/day`,
  BANK: (count) => `+${count * 2}% income`,
  HOME: (count) => `+${formatNumber(count * 8)} capacity`,
  MILL: (_count, pctLand, be) => {
    const effect = pctBased(1.5, pctLand, be) * 100;
    return `−${effect.toFixed(1)}% construction cost`;
  },
  TRAINING_GROUND: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% OME`;
  },
  FORT: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% DME`;
  },
  ARMOURY: (_count, pctLand, be) => {
    const reduction = pctSimple(1.5, pctLand, be) * 100;
    return `−${reduction.toFixed(1)}% train cost / +${reduction.toFixed(1)}% draft cost`;
  },
  GUARD_STATION: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% attack gains`;
  },
  MILITARY_BARRACK: () => 'capacity building',
  HOSPITAL: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `−${effect.toFixed(1)}% casualties`;
  },
  STABLE: (count, _pct, be) =>
    `+${formatNumber(Math.round(count * 1 * be))} horses/day, +${formatNumber(count * 60)} capacity`,
  DUNGEON: (count) => `+${formatNumber(count * 50)} capacity`,
  GUILD: (count, _pct, be) => `+${Math.round(count * 1 * be)} wizards/day`,
  TOWER: (count, _pct, be) => `+${formatNumber(Math.round(count * 12 * be))} runes/day`,
  SCHOOL: (_count, pctLand, be) => {
    const effect = pctBased(1.5, pctLand, be) * 100;
    return `−${effect.toFixed(1)}% science cost`;
  },
  LIBRARY: (_count, pctLand, be) => {
    const effect = pctSimple(2.0, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% science effectiveness`;
  },
  THIEVES_DEN: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% thievery effectiveness`;
  },
  WATCH_TOWER: (_count, pctLand, be) => {
    const effect = pctSimple(1.5, pctLand, be) * 100;
    return `+${effect.toFixed(1)}% thief protection`;
  },
};

// ─── Construction Planner Helpers ────────────────────────────────────────────

/**
 * Flat building order from BUILDING_GROUPS for consistent planner row ordering. NOT the same list
 * as `constants/buildings.ts`'s `BUILDING_ORDER` (classic-Utopia display order, includes `'NONE'`
 * for Barren Lands, used by Internal Affairs/Survey results) — that's a different, incompatible
 * sequence that happens to share a name today only because the two files never imported each
 * other. Renamed on extraction to avoid a real collision once both are imported into
 * `BuildingOverviewPanel.tsx`.
 */
export const PLANNER_BUILDING_ORDER = BUILDING_GROUPS.flatMap((g) => g.buildings.map((b) => b.id));

export interface PlannerData {
  days: number[];
  buildings: { id: string; name: string; byDay: Record<number, number> }[];
  totals: Record<number, number>;
}

export function buildPlannerData(construction: Construction[]): PlannerData | null {
  if (construction.length === 0) return null;

  // Collect per-building-per-day counts
  const map = new Map<string, Record<number, number>>();

  for (const c of construction) {
    const entry = map.get(c.building) ?? {};
    entry[c.ticksRemaining] = (entry[c.ticksRemaining] ?? 0) + c.acres;
    map.set(c.building, entry);
  }

  // Always show a full Utopian month (24 days)
  const days = Array.from({ length: 24 }, (_, i) => i + 1);

  // Order buildings by PLANNER_BUILDING_ORDER, only those with construction
  const orderedIds = PLANNER_BUILDING_ORDER.filter((id) => map.has(id));

  const buildings = orderedIds.map((id) => ({
    id,
    name: BUILDING_MAP[id]?.name ?? id,
    byDay: map.get(id)!,
  }));

  const totals: Record<number, number> = {};
  for (const day of days) {
    totals[day] = construction
      .filter((c) => c.ticksRemaining === day)
      .reduce((sum, c) => sum + c.acres, 0);
  }

  return { days, buildings, totals };
}

// ─── Exploration Planner Helpers ─────────────────────────────────────────────

export interface ExplorationPlannerData {
  days: number[];
  byDay: Record<number, number>;
}

export function buildExplorationPlannerData(exploration: Exploration[]): ExplorationPlannerData | null {
  if (exploration.length === 0) return null;

  const days = Array.from({ length: 24 }, (_, i) => i + 1);
  const byDay: Record<number, number> = {};

  for (const e of exploration) {
    byDay[e.ticksRemaining] = (byDay[e.ticksRemaining] ?? 0) + e.acres;
  }

  return { days, byDay };
}
